export interface RatingBreakdownData {
    overall?: number;
    overall_Count?: number;
    breakdownCriteria?: unknown[];
    ratingBreakdownAverage?: unknown[];
}

export interface ParsedRating {
    overall: number;
    count: number;
    displayRating: string | null;
    criteria: unknown[];
    criteriaAverage: unknown[];
}

export function parseMetaData(metaData: unknown): Record<string, unknown> {
    if (!metaData) return {};
    if (typeof metaData === 'string') {
        try {
            return JSON.parse(metaData) as Record<string, unknown>;
        } catch {
            return {};
        }
    }
    if (typeof metaData === 'object') {
        return metaData as Record<string, unknown>;
    }
    return {};
}

function normalizeBreakdown(raw: unknown): RatingBreakdownData | undefined {
    if (raw == null) return undefined;

    let value = raw;
    if (typeof value === 'string') {
        try {
            value = JSON.parse(value);
        } catch {
            return undefined;
        }
    }

    if (typeof value !== 'object') return undefined;

    const breakdown = value as Record<string, unknown>;
    const overall = Number(
        breakdown.overall ?? breakdown.Overall ?? breakdown.rating
    );
    const count = Number(
        breakdown.overall_Count ??
            breakdown.overall_count ??
            breakdown.overallCount ??
            breakdown.count
    );

    return {
        overall: Number.isFinite(overall) ? overall : undefined,
        overall_Count: Number.isFinite(count) ? count : undefined,
        breakdownCriteria: (breakdown.breakdownCriteria ??
            breakdown.breakdown_criteria) as unknown[] | undefined,
        ratingBreakdownAverage: (breakdown.ratingBreakdownAverage ??
            breakdown.rating_breakdown_average) as unknown[] | undefined,
    };
}

export function getRatingBreakdown(metaData: unknown): RatingBreakdownData | undefined {
    const meta = parseMetaData(metaData);
    return normalizeBreakdown(meta.ratingBreakdown ?? meta.rating_breakdown);
}

/** Matches workspace details: scale 0–100 overall to a 0–5 score. */
export function formatOverallRating(overall: number): string | null {
    if (!(overall > 0)) return null;
    return ((overall / 100) * 5).toFixed(1);
}

export function parseRatingFromMetaData(metaData: unknown): ParsedRating {
    const breakdown = getRatingBreakdown(metaData);
    const overall = Number(breakdown?.overall) || 0;
    const count = Number(breakdown?.overall_Count) || 0;

    return {
        overall,
        count,
        displayRating: formatOverallRating(overall),
        criteria: breakdown?.breakdownCriteria ?? [],
        criteriaAverage: breakdown?.ratingBreakdownAverage ?? [],
    };
}

